import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import clsx from "clsx";
import { atom } from "jotai";
import { isEmpty, keyBy } from "lodash";

import { usePageTitle } from "@/atoms/page-title-atom";
import { FETCH_SETTINGS_DETAILS } from "@/queries";
import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { type AdminLoggingContext } from "@components/v2/modals";
import { ClicksAndViewsEnhancedModellingBanner } from "@features/clicks-and-view-enhanced-modelling/clicks-and-views-enhanced-modelling";
import {
  allAdColours,
  CreativeAnalyticsProvider,
  useAvailableAdColoursAtom,
  useAvailableMetricsByIdAtom,
} from "@features/creative-analytics/atoms";
import { CreativeAnalyticsCardSleeve } from "@features/creative-analytics/components/ca-card-sleeve";
import { CreativeAnalyticsCharts } from "@features/creative-analytics/components/ca-charts";
import { CreativeAnalyticsControl } from "@features/creative-analytics/components/ca-control";
import { CreativeAnalyticsTypeBar } from "@features/creative-analytics/components/ca-creative-type-bar";
import { CreativeAnalyticsTopBar } from "@features/creative-analytics/components/ca-top-bar";
import { useReportBodyState } from "@features/creative-analytics/hooks/ca-control-state";
import { SalesViewProvider } from "@features/creative-analytics/hooks/saved-view";
import { CustomMetricsProvider } from "@features/custom-metrics/hooks/custom-metrics-provider";
import { useCustomMetrics } from "@features/custom-metrics/hooks/use-custom-metrics";
import { useAvailableMetricsWithArgs } from "@features/sales-v3/hooks/use-available-metrics";
import { FETCH_CUSTOM_GOALS } from "@gql/creative-analytics";
import { useNorthbeamQuery, useUpdateUserFeatureFlag } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  type FetchCustomGoals,
  type FetchSettingsDetails,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { Modal } from "@shared/modal";
import { usePrefs } from "@utils/client-side-preferences";

const useLoading = () => {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setTimeout(() => setLoading(false), 1);
  }, []);
  return loading;
};

export function CreativeAnalyticsPage() {
  const [, setPageTitle] = usePageTitle();
  const { user } = useUser();

  const pageLoading = useLoading();
  useEffect(() => {
    setPageTitle("Creative Analytics");
  }, [setPageTitle]);

  useEffect(() => {
    prettyLogEvent.visited({
      user,
      page: Pages.CREATIVE_ANALYTICS,
    });
  }, [user]);

  if (pageLoading) {
    return <LoadingSlide />;
  }

  return (
    <CustomMetricsProvider>
      <CreativeAnalyticsProvider>
        <SalesViewProvider>
          <MetricStorage>
            <CreativeAnalyticsPageInner />
          </MetricStorage>
        </SalesViewProvider>
      </CreativeAnalyticsProvider>
    </CustomMetricsProvider>
  );
}

const creativeAnalyticsQueryMetadata = atom<AdminLoggingContext>({
  bqLogs: [],
});

function MetricStorage({ children }: { children: React.ReactNode }) {
  const { loading, data: customGoalsData } =
    useNorthbeamQuery<FetchCustomGoals>(FETCH_CUSTOM_GOALS);

  const customGoals = useMemo(
    () => customGoalsData?.me.customGoals.goals ?? [],
    [customGoalsData?.me.customGoals],
  );
  const prefs = usePrefs();

  const { user } = useUser();
  const { attributionModel, attributionWindow } = useReportBodyState();
  const [availableMetricsById, setAvailableMetricsById] =
    useAvailableMetricsByIdAtom();

  const { customMetricData } = useCustomMetrics("accrual");

  const { metrics } = useAvailableMetricsWithArgs(
    {
      attributionModel,
      attributionWindow,
      accountingMode: "accrual",
      timeGranularity: "daily",
    },
    {
      showV1Forecasts: false,
      customGoals,
      noAttributionWindowTransparency: true,
    },
  );

  useEffect(() => {
    setAvailableMetricsById(
      keyBy([...metrics, ...(customMetricData ?? [])], "id"),
    );
  }, [
    user,
    attributionModel,
    attributionWindow,
    customGoals,
    setAvailableMetricsById,
    prefs.isDemoMode,
    customMetricData,
    metrics,
  ]);

  if (loading || isEmpty(availableMetricsById)) {
    return <LoadingSlide />;
  }
  return <>{children}</>;
}

function CreativeAnalyticsPageInner() {
  const ref = useRef<HTMLDivElement>(null);
  const { selectedAdsByAdKey } = useReportBodyState();
  const [, setAvailableColours] = useAvailableAdColoursAtom();
  const [queryMetadata, setQueryMetadata] = useState<AdminLoggingContext>({
    bqLogs: [],
  });

  const metadata = useMemo(() => {
    return {
      setQueryMetadata,
      clearMetadata: () => {
        setQueryMetadata({ bqLogs: [] });
      },
    };
  }, []);

  useEffect(() => {
    const usedAdColourIdsSet = new Set(
      Object.values(selectedAdsByAdKey).map(
        ({ adColouring }) => adColouring.id,
      ),
    );

    const remainingAdColours = allAdColours.filter(
      ({ id }) => !usedAdColourIdsSet.has(id),
    );

    setAvailableColours(remainingAdColours);
  }, [selectedAdsByAdKey, setAvailableColours]);

  return (
    <div className="relative h-full w-full flex flex-col overflow-hidden bg-v2-glass-inverse text-v2-primary">
      <EducationModal />
      {(() => {
        const banner = <ClicksAndViewsEnhancedModellingBanner />;
        return (
          banner && (
            <div className="p-2 bg-v2-primary">
              <div className="rounded-md overflow-hidden">{banner}</div>
            </div>
          )
        );
      })()}
      <CreativeAnalyticsControl />
      <div
        className={clsx("mt-5 relative grow flex flex-col mb-[3rem]")}
        ref={ref}
      >
        <div className="mx-4 relative bg-v2-primary rounded-md border border-v2-secondary flex flex-col grow">
          <CreativeAnalyticsTopBar />
          <CreativeAnalyticsTypeBar
            queryMetadata={{ ...metadata, metadata: queryMetadata }}
          />
          <CreativeAnalyticsCardSleeve
            queryMetadata={{ ...metadata, metadata: queryMetadata }}
          />
        </div>
      </div>
      <CreativeAnalyticsCharts />
    </div>
  );
}

function EducationModal() {
  const { data, loading } = useNorthbeamQuery<FetchSettingsDetails>(
    FETCH_SETTINGS_DETAILS,
    {},
  );

  const featureFlags = data?.me.featureFlags;
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setOpen(featureFlags?.showCreativeAnalyticsFirstTimeEducation);
  }, [featureFlags?.showCreativeAnalyticsFirstTimeEducation]);

  const [updateUserFeatureFlag] = useUpdateUserFeatureFlag();

  const onClose = useCallback(async () => {
    setOpen(false);
    await updateUserFeatureFlag(
      "showCreativeAnalyticsFirstTimeEducation",
      false,
    );
  }, [updateUserFeatureFlag]);

  if (loading) {
    return null;
  }

  const currentVideoUrl = new URL(
    "https://www.youtube.com/embed/XulhY8o1cUU?si=bzbrsPRfGUcecweK",
  );
  // Don't show related videos
  currentVideoUrl.searchParams.set("rel", "0");
  // Don't show video title
  currentVideoUrl.searchParams.set("showinfo", "0");
  // Don't show progress bar
  currentVideoUrl.searchParams.set("autohide", "1");

  const newVideoUrl = currentVideoUrl.toString();

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      titleContent={
        <div className="text-lg font-semibold">
          Welcome to Creative Analytics!
        </div>
      }
    >
      <p>
        We are excited to release this new feature where you can quickly view,
        compare, and measure your creative performance at a glance.
      </p>
      <p>
        Please watch this quick instructional video, and if you have any
        questions reach out to your customer success team member or email us at{" "}
        <a href="mailto:success@northbeam.io" target="_blank" rel="noreferrer">
          success@northbeam.io
        </a>
      </p>
      <div className="mt-5 font-semibold">
        What you need to run creative analytics:
      </div>
      <ul className="list-disc ml-10">
        <li>Have adblocker turned off</li>
      </ul>
      <iframe
        width="100%"
        height="350px"
        src={newVideoUrl}
        title="YouTube video player"
        frameBorder="0"
        allow={
          "accelerometer; clipboard-write; encrypted-media; gyroscope; " +
          "picture-in-picture; web-share"
        }
        allowFullScreen
      />
      <div className="mt-5">
        <a
          href="https://docs.northbeam.io/docs/creative-analytics-walkthrough"
          target="_blank"
          rel="noreferrer"
        >
          Knowledge Base link
        </a>
      </div>
    </Modal>
  );
}
